import React from "react"
import { connect } from "react-redux"
import PropTypes from "prop-types"

import { datumHeaderCellConnection } from "app/static/frontend/sheets/selectors/column-selectors"
// action creators
import {
    clearFilter,
    clearSort,
    editSheet,
    patchSheet,
    sortByColumnAtIndex,
    toggleDefaultSortOnColumn,
} from "app/static/frontend/sheets/sheet-action-creators"

// components
import ResourceTableColumnSearchTrigger from "app/static/frontend/sheets/components/ResourceTableColumnSearchTrigger"
import ResourceTableColumnSortingIndicator from "app/static/frontend/sheets/components/ResourceTableColumnSortingIndicator"
import Tooltip from "app/static/frontend/components/Tooltip"

// constants
import sheetConstants from "app/static/frontend/sheets/constants"
import { Icon as DSIcon } from "quorumdesign"

// styles
import "app/static/stylus/sheet.styl"

/**
 * The ResourceTableDatumHeaderCell component renders the DatumHeaderCell that should be
 * displayed specifically for resource tables.
 * This component is seperated from DatumHeaderCell in order to reduce the chance of regressions in the future.
 * This is run on L179 on components/Sheet.jsx
 */
export const ResourceTableDatumHeaderCell = ({
    canSetDefaultSort,
    canSort,
    clearFilter,
    clearSort,
    datumHeaderTitle,
    defaultSortingType,
    disableDatumFilter,
    id,
    isDefaultSorting,
    resourceTableDatumSearchTrigger,
    searching,
    sortByColumnAtIndex,
    sortType,
    sorting,
    toggleDefaultSortOnColumn,
    tooltipText,
}) => {
    // this component is responsible for rendering the search component for the column
    const renderSearchTrigger = () => (
        <ResourceTableColumnSearchTrigger
            canSearch
            columnIndex={sheetConstants.DATUM_COLUMN_INDEX} // the columnIndex is a constant here
            id={id}
            isSearching={searching}
            dataCy="datum-column-filter"
            resourceTableDatumSearchTrigger={resourceTableDatumSearchTrigger}
        />
    )

    const renderSortTrigger = () => (
        <ResourceTableColumnSortingIndicator
            canSort={canSort}
            clearSort={clearSort}
            columnIndex={sheetConstants.DATUM_COLUMN_INDEX}
            columnId={undefined}
            id={id}
            sortByColumnAtIndex={sortByColumnAtIndex}
            sorting={sorting}
            sortType={sortType}
            canSetDefaultSort={canSetDefaultSort}
            isDefaultSorting={isDefaultSorting}
            defaultSortingType={defaultSortingType}
            toggleDefaultSortOnColumn={toggleDefaultSortOnColumn}
        />
    )

    // function that renders the clear filters button for the header columns
    const renderClearSearch = () => (
        <Tooltip label="Click to Clear Filtering">
            <div className="cancel-button">
                <DSIcon
                    icon="times-circle"
                    iconFamily="far"
                    onClick={() => clearFilter(id, sheetConstants.DATUM_COLUMN_INDEX)}
                />
            </div>
        </Tooltip>
    )

    return (
        <div className="datum-header-cell-inner-wrapper">
            <span className="column-name">
                {tooltipText && (
                    <Tooltip label={tooltipText}>
                        <div className="datum-cell-title">{datumHeaderTitle}</div>
                    </Tooltip>
                )}
                {!tooltipText && <div className="datum-cell-title">{datumHeaderTitle}</div>}
                <div className="rt-column-options">
                    {canSort && renderSortTrigger()}
                    {!disableDatumFilter && renderSearchTrigger()}
                    {searching && renderClearSearch()}
                </div>
            </span>
        </div>
    )
}

ResourceTableDatumHeaderCell.propTypes = {
    canSetDefaultSort: PropTypes.bool,
    canSort: PropTypes.bool,
    clearFilter: PropTypes.func.isRequired,
    clearSort: PropTypes.func,
    datumHeaderTitle: PropTypes.node,
    defaultSortingType: PropTypes.number,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    isDefaultSorting: PropTypes.bool,
    resourceTableDatumSearchTrigger: PropTypes.object,
    searching: PropTypes.bool.isRequired,
    sortByColumnAtIndex: PropTypes.func,
    sortType: PropTypes.number,
    toggleDefaultSortOnColumn: PropTypes.func,
    tooltipText: PropTypes.string,
}

ResourceTableDatumHeaderCell.defaultProps = {
    // these are passed by the parent component, so just make sure there is something defined
    datumCellStyles: {
        columnIcons: {
            width: "101px",
        },
        columnOptions: {
            right: "-100px",
        },
    },
}

export default connect((state, props) => datumHeaderCellConnection(state, props), {
    clearFilter,
    clearSort,
    editSheet,
    patchSheet,
    sortByColumnAtIndex,
    toggleDefaultSortOnColumn,
})(ResourceTableDatumHeaderCell)
